import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Nav, 
  Form, 
  Button, 
  Alert,
  Spinner,
  ButtonGroup
} from 'react-bootstrap';
import * as Icons from 'react-icons/fa';
import { contentAPI } from '../services/api';
import Icon from '../components/Icon';

// Content page types
const PAGE_TYPES = [
  { id: 'home', label: 'Home Page' },
  { id: 'about', label: 'About Page' },
  { id: 'products', label: 'Products Page' },
  { id: 'services', label: 'Services Page' },
  { id: 'partners', label: 'Partners Page' },
  { id: 'gallery', label: 'Gallery Page' },
  { id: 'contact', label: 'Contact Page' },
  { id: 'footer', label: 'Footer' }
];

const LANGUAGES = [
  { id: 'EN', label: 'English' },
  { id: 'AR', label: 'Arabic' }
];

function parsePath(path: string): (string|number)[] {
  // Split by dot, but convert [n] to number indices
  return path.split('.').flatMap(part => {
    const match = part.match(/^([a-zA-Z0-9_]+)\[(\d+)\]$/);
    if (match) {
      return [match[1], Number(match[2])];
    }
    return part;
  });
}

const ContentManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [activeLanguage, setActiveLanguage] = useState('EN');
  const [pageContent, setPageContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState<any>({});

  // Fetch page content on tab or language change
  useEffect(() => {
    fetchPageContent(activeTab, activeLanguage);
  }, [activeTab, activeLanguage]);

  // Function to fetch content for a specific page and language
  const fetchPageContent = async (pageType: string, language: string) => {
    setLoading(true);
    setError('');
    try {
      const response = await contentAPI.getPageContent(pageType, language);
      setPageContent(response.data);
      setFormData(response.data);
    } catch (err: any) {
      console.error('Error fetching content:', err);
      setError(`Failed to load ${pageType} page content in ${language}: ${err.message || 'Unknown error'}`);
      setPageContent(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle input change
  const handleInputChange = (path: string, value: any) => {
    setFormData((prevData: any) => {
      const newData = JSON.parse(JSON.stringify(prevData));
      const pathParts = parsePath(path);

      let current = newData;
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        const nextPart = pathParts[i + 1];
        if (typeof nextPart === 'number') {
          if (!Array.isArray(current[part])) current[part] = [];
        } else {
          if (!current[part]) current[part] = {};
        }
        current = current[part];
      }
      current[pathParts[pathParts.length - 1]] = value;
      return newData;
    });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await contentAPI.updatePageContent(activeTab, formData, activeLanguage);
      setSuccess(`${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} page content updated successfully in ${activeLanguage}!`);
      setPageContent(formData);
    } catch (err: any) {
      console.error('Error updating content:', err);
      setError(`Failed to update content: ${err.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  // Recursive function to render form fields based on content structure
  const renderFormFields = (data: any, path = '', level = 0) => {
    if (!data) return null;
    
    return Object.entries(data).map(([key, value]: [string, any]) => {
      const currentPath = path ? `${path}.${key}` : key;
      
      // If value is a simple type (string, number, boolean), render an input field
      if (typeof value === 'string') {
        return (
          <Form.Group key={currentPath} className="mb-3">
            <Form.Label style={{fontWeight: level === 0 ? 'bold' : 'normal'}}>
              {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
            </Form.Label>
            <Form.Control
              as={value.length > 50 ? "textarea" : "input"}
              {...(value.length > 50 ? { rows: value.length > 100 ? 4 : 2 } : {})}
              value={value}
              onChange={(e) => handleInputChange(currentPath, e.target.value)}
              dir={activeLanguage === 'AR' ? 'rtl' : 'ltr'}
            />
          </Form.Group>
        );
      }
      // If value is a number, render a number input
      else if (typeof value === 'number') {
        return (
          <Form.Group key={currentPath} className="mb-3">
            <Form.Label style={{fontWeight: level === 0 ? 'bold' : 'normal'}}>
              {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
            </Form.Label>
            <Form.Control
              type="number"
              value={value}
              onChange={(e) => handleInputChange(currentPath, Number(e.target.value))}
            />
          </Form.Group>
        );
      }
      // If value is a boolean, render a checkbox
      else if (typeof value === 'boolean') {
        return (
          <Form.Group key={currentPath} className="mb-3">
            <Form.Check
              type="checkbox"
              label={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
              checked={value}
              onChange={(e) => handleInputChange(currentPath, e.target.checked)}
            />
          </Form.Group>
        );
      }
      // If value is an array of objects, render a section for each item
      else if (Array.isArray(value)) {
        // Check if array is of primitives (string, number, boolean)
        const isPrimitiveArray = value.every(
          (item) =>
            typeof item === "string" ||
            typeof item === "number" ||
            typeof item === "boolean"
        );

        return (
          <div key={currentPath} className="mb-4">
            <h5 className="border-bottom pb-2 mt-4">
              {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1")}
            </h5>
            {isPrimitiveArray
              ? value.map((item, index) => (
                  <Form.Group key={`${currentPath}[${index}]`} className="mb-2">
                    <Form.Label>
                      {`Item ${index + 1}`}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={item.toString()}
                      onChange={(e) =>
                        handleInputChange(`${currentPath}[${index}]`, e.target.value)
                      }
                    />
                  </Form.Group>
                ))
              : value.map((item, index) => (
                  <Card key={`${currentPath}-${index}`} className="mb-3 shadow-sm">
                    <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                      <span>Item {index + 1}</span>
                    </Card.Header>
                    <Card.Body>
                      {renderFormFields(item, `${currentPath}[${index}]`, level + 1)}
                    </Card.Body>
                  </Card>
                ))}
          </div>
        );
      }
      // If value is an object, render a nested form section
      else if (typeof value === 'object' && value !== null) {
        return (
          <div key={currentPath} className={`mb-4 ${level > 0 ? 'ps-3 border-start' : ''}`}>
            <h5 className="border-bottom pb-2 mt-3">
              {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
            </h5>
            {renderFormFields(value, currentPath, level + 1)}
          </div>
        );
      }
      
      return null;
    });
  };

  return (
    <Container fluid>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1 className="mb-0">Content Management</h1>
          <p className="text-muted">Manage content for various pages of the website</p>
        </Col>
        <Col xs="auto">
          <ButtonGroup>
            {LANGUAGES.map((lang) => (
              <Button
                key={lang.id}
                variant={activeLanguage === lang.id ? 'primary' : 'outline-primary'}
                onClick={() => setActiveLanguage(lang.id)}
              >
                {lang.label}
              </Button>
            ))}
          </ButtonGroup>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white py-3">
          <Nav variant="tabs" className="flex-row">
            {PAGE_TYPES.map((page) => (
              <Nav.Item key={page.id}>
                <Nav.Link 
                  active={activeTab === page.id}
                  onClick={() => setActiveTab(page.id)}
                  className="px-3 py-2"
                >
                  {page.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading content...</p>
            </div>
          ) : error && !pageContent ? (
            <Alert variant="danger">
              <Icon icon={Icons.FaExclamationTriangle} className="me-2" />
              {error}
            </Alert>
          ) : (
            <Form onSubmit={handleSubmit}>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              
              {renderFormFields(formData)}
              
              <div className="d-flex justify-content-end mt-4">
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={saving}
                  className="d-flex align-items-center"
                >
                  {saving && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                  <Icon icon={Icons.FaSave} className="me-2" />
                  Save Changes
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ContentManagement; 